import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

/**
 * Assessment CMS — the parent-landing question banks.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS EDITS
 *
 * The questions a parent and their child answer on the public landing page at
 * /auth/parent-landing. Until now they were ~2,700 lines of JavaScript in the
 * frontend repo, so every wording fix was a pull request and a deploy. They now
 * live in four database tables and this is the screen that edits them.
 *
 * Three banks, distinguished by the subject's `track`:
 *
 *   foundation  Class 1-4. Five subjects x two class groups x 3-4 levels, each
 *               level a ladder rung the child has to pass to reach the next.
 *   kg          LKG/UKG. Ten picture questions, one level, no pass gate.
 *   parent      The ten questions every parent answers before any paper —
 *               five scored, five profiling.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY THUNK CHECKS `status`
 *
 * api/request.js RESOLVES on failure — it returns `err.response.data` from the
 * catch rather than rethrowing. So `await` cannot distinguish a 400 from a 200
 * and a rejected thunk never happens for a server-side error. Every mutation
 * here therefore inspects `res?.status` itself and rejects explicitly, so the
 * component's `.unwrap()` means what it looks like it means.
 *
 * That matters more here than in most slices, because the server's rejections
 * carry the validation messages that are the whole point of the screen — "these
 * scores are inverted", "this answer is not one of the options". Swallowing
 * them would leave the editor with a form that silently does nothing.
 */

const initialState = {
  // The navigation tree: subjects -> levels, with per-level question counts.
  // No questions — the tree is drawn before we know which level is wanted.
  tree: {
    data: [],
    // The five paper types and the class groups each one can be split into,
    // served by the API so the create forms cannot drift from the validator.
    // `secondary` (Class 5-10) and `higher_secondary` (+1, +2) ship with no
    // rows at all — an employee builds them from here.
    tracks: [],
    // The rungs a ladder paper may have, and the position each one occupies.
    // Served for the same reason as `tracks`: the level form offers exactly
    // what the validator accepts, so a rung it shows can always be saved.
    levels: [],
    loading: false,
  },
  // One level's questions, the editor's working set.
  level: {
    level: null,
    subject: null,
    questions: [],
    totalMarks: 0,
    remainingSlots: 0,
    loading: false,
  },
  // Live validation of the question being typed. `warnings` are advisory and
  // `accepted` previews what the grader will actually match.
  draft: {
    valid: true,
    errors: [],
    warnings: [],
    accepted: [],
    checking: false,
  },
  saving: false,
};

const BASE = 'student-portal/cms/assessment';

/**
 * The server answers `{status, message, data, meta}` and never throws. Turn a
 * non-success into a real rejection so callers can `.unwrap()`.
 */
const unwrapOrThrow = (res, rejectWithValue) => {
  if (res?.status === 'success') return res;
  return rejectWithValue({
    message: res?.message || 'Request failed',
    errors: res?.data?.errors || [],
    warnings: res?.data?.warnings || [],
  });
};

/* ------------------------------------------------------------------ */
/* Reads                                                               */
/* ------------------------------------------------------------------ */

export const getAssessmentTree = createAsyncThunk(
  'assessmentCms/getAssessmentTree',
  async () => {
    const res = await apiRequest({ method: 'get', url: `${BASE}/subjects` });
    return res;
  }
);

export const getLevelQuestions = createAsyncThunk(
  'assessmentCms/getLevelQuestions',
  async (levelId) => {
    const res = await apiRequest({
      method: 'get',
      url: `${BASE}/levels/${levelId}/questions`,
    });
    return res;
  }
);

/* ------------------------------------------------------------------ */
/* Writes                                                              */
/* ------------------------------------------------------------------ */

/**
 * Dry run. Same checks as the save, nothing written. Called as the editor types
 * so the spaced-slash warning and the accepted-answers preview appear before
 * they press Save rather than after.
 */
export const validateQuestionDraft = createAsyncThunk(
  'assessmentCms/validateQuestionDraft',
  async (payload) => {
    const res = await apiRequest({
      method: 'post',
      url: `${BASE}/questions/validate`,
      data: payload,
    });
    return res;
  }
);

/**
 * Create (no `id`) or update (with `id`). A question and its options save
 * together as one thing — the server replaces the option list inside the same
 * transaction, so there is no window where a question has half its choices.
 */
export const saveQuestion = createAsyncThunk(
  'assessmentCms/saveQuestion',
  async ({ id, ...payload }, { rejectWithValue }) => {
    const res = await apiRequest({
      method: id ? 'patch' : 'post',
      url: id ? `${BASE}/questions/${id}` : `${BASE}/questions`,
      data: payload,
    });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

export const deleteQuestion = createAsyncThunk(
  'assessmentCms/deleteQuestion',
  async (id, { rejectWithValue }) => {
    const res = await apiRequest({ method: 'delete', url: `${BASE}/questions/${id}` });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

export const reorderQuestions = createAsyncThunk(
  'assessmentCms/reorderQuestions',
  async ({ levelId, order }, { rejectWithValue }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `${BASE}/levels/${levelId}/reorder`,
      data: { order },
    });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

/**
 * Create a paper.
 *
 * This is what makes a new track usable without a migration. `keyName` is
 * derived from the label server-side ("Social Science" -> "social-science") and
 * is immutable afterwards — it is what the browser sends as `?subject=` and
 * what every stored answer is filed under.
 */
export const createSubject = createAsyncThunk(
  'assessmentCms/createSubject',
  async (payload, { rejectWithValue }) => {
    const res = await apiRequest({
      method: 'post',
      url: `${BASE}/subjects`,
      data: payload,
    });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

/** Refused by the server while the paper still has questions. */
export const deleteSubject = createAsyncThunk(
  'assessmentCms/deleteSubject',
  async (subjectId, { rejectWithValue }) => {
    const res = await apiRequest({ method: 'delete', url: `${BASE}/subjects/${subjectId}` });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

/**
 * Create a level under a paper, for one class.
 *
 * `levelOrder` is no longer sent at all. The server derives it from `levelKey`,
 * which the form picks from the catalogue the tree endpoint ships — so Basic is
 * position 1 in every paper, and the one number that can never be corrected
 * afterwards is no longer something a person can type.
 *
 * `levelKey: 'single'` is the "no levels" case: one ungated level standing in
 * for none, because a question with no level has no id to file its answer under.
 */
export const createLevel = createAsyncThunk(
  'assessmentCms/createLevel',
  async (payload, { rejectWithValue }) => {
    const res = await apiRequest({ method: 'post', url: `${BASE}/levels`, data: payload });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

/** Refused by the server while the level still has questions. */
export const deleteLevel = createAsyncThunk(
  'assessmentCms/deleteLevel',
  async (levelId, { rejectWithValue }) => {
    const res = await apiRequest({ method: 'delete', url: `${BASE}/levels/${levelId}` });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

export const updateLevel = createAsyncThunk(
  'assessmentCms/updateLevel',
  async ({ levelId, ...payload }, { rejectWithValue }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `${BASE}/levels/${levelId}`,
      data: payload,
    });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

export const updateSubject = createAsyncThunk(
  'assessmentCms/updateSubject',
  async ({ subjectId, ...payload }, { rejectWithValue }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `${BASE}/subjects/${subjectId}`,
      data: payload,
    });
    return unwrapOrThrow(res, rejectWithValue);
  }
);

const assessmentCmsSlice = createSlice({
  name: 'assessmentCms',
  initialState,
  reducers: {
    // Clearing on dialog open stops the previous question's warnings from
    // appearing against a fresh, empty form.
    clearDraftValidation: (state) => {
      state.draft = { ...initialState.draft };
    },
  },
  extraReducers: (builder) => {
    /* ----- tree ----- */
    builder.addCase(getAssessmentTree.pending, (state) => {
      state.tree.loading = true;
    });
    builder.addCase(getAssessmentTree.fulfilled, (state, action) => {
      state.tree.loading = false;
      state.tree.data = action?.payload?.data || [];
      state.tree.tracks = action?.payload?.meta?.tracks || [];
      state.tree.levels = action?.payload?.meta?.levels || [];
    });
    builder.addCase(getAssessmentTree.rejected, (state) => {
      state.tree.loading = false;
    });

    /* ----- one level ----- */
    builder.addCase(getLevelQuestions.pending, (state) => {
      state.level.loading = true;
    });
    builder.addCase(getLevelQuestions.fulfilled, (state, action) => {
      const data = action?.payload?.data || {};
      state.level = {
        level: data.level || null,
        subject: data.subject || null,
        questions: data.questions || [],
        totalMarks: data.totalMarks || 0,
        remainingSlots: data.remainingSlots || 0,
        loading: false,
      };
    });
    builder.addCase(getLevelQuestions.rejected, (state) => {
      state.level.loading = false;
    });

    /* ----- live validation ----- */
    builder.addCase(validateQuestionDraft.pending, (state) => {
      state.draft.checking = true;
    });
    builder.addCase(validateQuestionDraft.fulfilled, (state, action) => {
      const data = action?.payload?.data || {};
      state.draft = {
        valid: data.valid !== false,
        errors: data.errors || [],
        warnings: data.warnings || [],
        accepted: data.accepted || [],
        checking: false,
      };
    });
    builder.addCase(validateQuestionDraft.rejected, (state) => {
      state.draft.checking = false;
    });

    /* ----- saving ----- */
    builder.addCase(saveQuestion.pending, (state) => {
      state.saving = true;
    });
    builder.addCase(saveQuestion.fulfilled, (state, action) => {
      state.saving = false;
      // Surface the server's warnings on a successful save too. A saved
      // question can still be worth a second look — "1/2 will be accepted as
      // one exact answer" is not an error, but the editor should see it.
      state.draft.warnings = action?.payload?.data?.warnings || [];
    });
    builder.addCase(saveQuestion.rejected, (state, action) => {
      state.saving = false;
      state.draft.errors = action?.payload?.errors || [];
      state.draft.valid = false;
    });
  },
});

export const { clearDraftValidation } = assessmentCmsSlice.actions;
export default assessmentCmsSlice.reducer;
